// @flow
import React, { useCallback, useState } from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { Paragraph } from '@rover/kibble/components';
import Button, { ButtonType } from '@rover/react-lib/components/buttons/Button';
import TextInput from '@rover/react-lib/components/formFields/TextInput';
import HorizontalLayout from '@rover/react-lib/components/layouts/HorizontalLayout';
import VerticalLayout from '@rover/react-lib/components/layouts/VerticalLayout';
import IntlPhoneInputContainer from '@rover/react-lib/containers/IntlPhoneInputContainer';
import { ContactInfoConstants } from '@rover/shared/js/constants/translation';

import SectionSaveButton from '../common/SectionSaveButton';
import SectionTitle from '../common/SectionTitle';

type Props = {
  personSummary: any,
  savePersonSummaryPrivate: ({ personData: any, personUrl: string }) => void,
  isContactComplete: boolean,
};

const SectionWrapper: StyledComponent<{}> = styled.div`
  margin: 0px 45px 15px 45px;
`;

const UpdateButton: StyledComponent<React$ElementConfig<typeof Button>> = styled(Button)`
  padding: 0;
`;

const FormWrapper: StyledComponent<{}> = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTextInput: StyledComponent<React$ElementConfig<typeof TextInput>> = styled(TextInput)`
  flex-grow: 0.4;
`;

const renderForm = (
  contactName,
  setContactName,
  phoneNumber,
  setPhoneValidation,
  phoneIsValid,
  submitEmergencyContactForm,
  phoneValidationMessage
) => (
  <SectionWrapper>
    <SectionTitle isComplete={false} titleText={ContactInfoConstants.emergencyContactInfo} />
    <FormWrapper>
      <StyledTextInput
        label={ContactInfoConstants.contactName}
        value={contactName}
        onChange={setContactName}
      />
      <IntlPhoneInputContainer
        validationError={phoneValidationMessage}
        onChange={setPhoneValidation}
      />
    </FormWrapper>
    <SectionSaveButton
      isComplete={false}
      buttonText={ContactInfoConstants.save}
      saveMethod={submitEmergencyContactForm}
    />
  </SectionWrapper>
);

const renderComplete = (
  emergencyContactPhoneNumber,
  emergencyContactName,
  toggleContactInfoForm
) => (
  <SectionWrapper>
    <VerticalLayout>
      <SectionTitle isComplete titleText={ContactInfoConstants.emergencyContactInfo} />
      <HorizontalLayout>
        <Paragraph marginBottom="1x">
          {emergencyContactName} {emergencyContactPhoneNumber}
        </Paragraph>
      </HorizontalLayout>
      <UpdateButton buttonType={ButtonType.TEXT} onClick={() => toggleContactInfoForm()}>
        {ContactInfoConstants.updateEmergencyContactInfo}
      </UpdateButton>
    </VerticalLayout>
  </SectionWrapper>
);

const ContactInfo = (props: Props) => {
  const { personSummary, savePersonSummaryPrivate, isContactComplete } = props;

  const emergencyContactName = personSummary.private.emergencyContactName || '';
  const emergencyContactPhoneNumber = personSummary.private.emergencyContactPhoneNumber || '';
  const [overrideContactFormVisible, setOverrideContactFormVisible] = useState(false);
  const [contactName, setContactName] = useState(emergencyContactName);
  const [phoneNumber, setPhoneNumber] = useState(emergencyContactPhoneNumber);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [phoneValidationMessage, setPhoneValidationMessage] = useState('');

  const setPhoneValidation = useCallback((validationNumber: string, validated: boolean) => {
    setPhoneValidationMessage('');
    setPhoneNumber(validationNumber);
    setPhoneIsValid(validated);
  });

  const resetForm = () => {
    setOverrideContactFormVisible(false);
    setPhoneIsValid(false);
    setPhoneValidationMessage('');
  };

  const submitEmergencyContactForm = () => {
    if (!phoneIsValid) {
      setPhoneValidationMessage(ContactInfoConstants.phoneNumberInvalid);
    }

    if (contactName.length !== 0 && phoneIsValid) {
      const personData = {
        emergencyContactPhoneNumber: phoneNumber,
        emergencyContactName: contactName,
      };
      savePersonSummaryPrivate({ personData, personUrl: personSummary.urls.private });
      resetForm();
    }
  };

  const mainContent =
    overrideContactFormVisible || !isContactComplete
      ? renderForm(
          contactName,
          setContactName,
          phoneNumber,
          setPhoneValidation,
          phoneIsValid,
          submitEmergencyContactForm,
          phoneValidationMessage
        )
      : renderComplete(emergencyContactPhoneNumber, emergencyContactName, () =>
          setOverrideContactFormVisible(!overrideContactFormVisible)
        );
  return <div>{mainContent}</div>;
};

export default ContactInfo;



// WEBPACK FOOTER //
// ../react-lib/src/pages/conversations/EmergencyFormPage/components/ContactInfo/ContactInfo.jsx